import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookOpen, Lock, Video } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-end justify-center overflow-hidden pb-6">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/growling-griffon-hero-v3-4k.jpg"
            alt="The Growling Griffon Tavern"
            className="w-full h-full object-cover hero-pan-animation"
            style={{objectPosition: '50% 22%'}}
          />
          <div className="absolute inset-0" style={{background: 'linear-gradient(to top, hsl(var(--background)) 0%, hsl(var(--background) / 0.92) 12%, hsl(var(--background) / 0.6) 26%, hsl(var(--background) / 0.25) 38%, transparent 52%)'}}></div>
        </div>
        
        <div className="relative z-10 text-center px-6 w-full max-w-6xl" data-testid="hero-content">
          <h1 className="text-5xl lg:text-6xl font-serif tracking-tighter text-primary mb-3 drop-shadow-lg">
            The Growling Griffon
          </h1>
          
          {/* Scrolling Story Container */}
          <div className="relative h-40 overflow-hidden mb-2 rounded-sm">
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent via-black/20 to-black/40 pointer-events-none z-10"></div>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none z-10"></div>
            
            <div className="scrolling-story-text text-base sm:text-lg lg:text-xl font-sans text-foreground leading-7 sm:leading-8 lg:leading-9 px-6 py-4">
              <p className="mb-4">Welcome to the Growling Griffon, your gateway to a 40-year-old world affectionately known to its players as the Grey Hawk Demon Cross Campaign.</p>
              
              <p className="mb-4">Push open the nail-studded door and step inside. The air is thick with tobacco and woodsmoke perspiration, beer, bread, and the low hum of stories being traded for clout and coin.</p>
              
              <p className="mb-4">At the heart of it all stands Big Jim, the Griffon's proprietor. He has poured drinks for heroes and villains alike, watched fortunes rise and fall, and remembers far more than he ever lets on. If you stay long enough, you may notice that nothing of consequence happens in this world without passing, in some form, through his doors.</p>
              
              <p className="mb-4">This site is your seat at one of these tables.</p>
              
              <p className="mb-4">Here, the long-running saga of the Grey Hawk Demon Cross Campaign unfolds, not as a single tale, but as a living world shaped by decades of adventure. What began as a game has become a layered history of characters, conflicts, alliances, betrayals, and legends that refuse to stay buried.</p>
              
              <ul className="mb-4 text-left max-w-2xl mx-auto space-y-2">
                <li>Explore the Glossary, where names are not just names but stories waiting to be uncovered</li>
                <li>Meet the cast of characters, from wandering laborers to ancient powers moving unseen</li>
                <li>Watch recorded tales and retellings, where moments from the campaign are brought back to life</li>
                <li>Piece together the hidden threads that connect tavern whispers to world-shaping events</li>
              </ul>
              
              <p className="mb-4">Some entries will answer your questions. Others will raise better ones.</p>
              
              <p className="mb-4">Nothing here is truly isolated. A passing mention in one corner of the world may echo loudly in another. The Griffon rewards those who linger, listen, and look twice.</p>
              
              <p className="mb-4">So take a seat. Order something strong. And keep your ears open.</p>
              
              <p className="mb-4">Because in the Growling Griffon, the story is still being told.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-card" data-testid="features-section">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl lg:text-4xl font-serif tracking-tight text-center mb-16 text-foreground">
            Discover a World of Lore
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-background border border-border rounded-sm hover:border-primary/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-wiki">
              <BookOpen className="w-12 h-12 text-primary mb-4" aria-hidden="true" />
              <h3 className="text-xl sm:text-2xl font-serif text-foreground mb-3">Interactive Glossary</h3>
              <p className="text-base font-sans text-foreground/90 leading-relaxed">
                Explore detailed articles about the world of Greyhawk, its locations, characters, and history.
              </p>
            </div>
            
            <div className="p-8 bg-background border border-border rounded-sm hover:border-primary/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-video">
              <Video className="w-12 h-12 text-primary mb-4" aria-hidden="true" />
              <h3 className="text-xl sm:text-2xl font-serif text-foreground mb-3">Embedded Videos</h3>
              <p className="text-base font-sans text-foreground/90 leading-relaxed">
                Watch videos and visual guides that bring the fantasy world to life with immersive storytelling.
              </p>
            </div>
            
            <div className="p-8 bg-background border border-border rounded-sm hover:border-primary/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-members">
              <Lock className="w-12 h-12 text-primary mb-4" aria-hidden="true" />
              <h3 className="text-xl sm:text-2xl font-serif text-foreground mb-3">Members Only</h3>
              <p className="text-base font-sans text-foreground/90 leading-relaxed">
                Unlock exclusive content and deep lore available only to registered members of the community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6" data-testid="cta-section">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl lg:text-4xl font-serif tracking-tight text-foreground mb-6">
            Begin Your Journey
          </h2>
          <p className="text-base font-sans text-foreground/90 leading-relaxed mb-8">
            Join our community and gain access to exclusive lore, stories, and content from the world of The Growling Griffon.
          </p>
          <Link to="/register" data-testid="cta-join-button">
            <Button size="lg" className="font-sans text-lg px-8 py-6 rounded-sm">
              Create Your Account
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
